import { INavData } from "@coreui/angular";
import { environment } from "../../../environments/environment";
import { TranslateService } from "@ngx-translate/core";
import { MigrationMode } from "../../services/migration-mode.service";
import { pageKeyForPath } from "../../login/pages";

export function getNavItems(
  translate: TranslateService,
  mode: MigrationMode = "legacy",
  companyType?: string | null,
  onboardingActive = false,
): INavData[] {
  const normalizedType = (companyType || "").toLowerCase().trim();
  const isTransferCompany = normalizedType === "transfer";
  const isRestaurantCompany = normalizedType === "restaurant";
  const isDefaultPlusCompany =
    normalizedType.replace(/[\s_-]+/g, "") === "defaultplus";

  return [
    {
      title: true,
      name: translate.instant("Navigation.USER_SECTION"), // "Kullanıcı"
    },
    {
      name: translate.instant("Navigation.DASHBOARD"),
      url: "/dashboard",
      iconComponent: { name: "cil-speedometer" },
    },
    ...(environment.showWebsiteManagement
      ? [
          {
            name: translate.instant("Navigation.WEBSITE_MANAGEMENT"),
            url: "/pages/website-management",
            iconComponent: { name: "cil-globe-alt" },
          },
        ]
      : []),
    {
      name: translate.instant("Navigation.DOWNLOAD"),
      url: "/pages/download",
      iconComponent: { name: "cil-cloud-download" },
    },
    {
      name: translate.instant("Navigation.MEETINGS"),
      url: "/pages/meetings",
      iconComponent: { name: "cil-video" },
    },
    ...(environment.showApiSource
      ? [
          {
            name: translate.instant("Navigation.DEVELOPERS"),
            url: "/pages/developers",
            iconComponent: { name: "cil-code" },
          },
        ]
      : []),

    {
      title: true,
      name: translate.instant("Navigation.STATISTICS_SECTION"),
    },
    {
      name: translate.instant("Navigation.STATISTICS"),
      url: "/pages/statistics",
      iconComponent: { name: "cil-bar-chart" },
    },
    {
      name: translate.instant("Navigation.CUSTOMERS"),
      url: "/pages/customers",
      iconComponent: { name: "cil-people" },
    },
    {
      name: translate.instant("MARKETING.TITLE"),
      url: "/pages/marketing",
      iconComponent: { name: "cil-envelope-open" },
    },

    {
      title: true,
      name: translate.instant("Navigation.CHANNELS_SECTION"), // "Kanallar"
    },
    ...(environment.showTelegram
      ? [
          {
            name: translate.instant("Navigation.TELEGRAM"),
            url: "/pages/telegram_bot",
            iconComponent: { name: "cib-telegram-plane" },
          },
        ]
      : []),
    ...(environment.showWhatsapp
      ? [
          {
            name: translate.instant("Navigation.WHATSAPP"),
            url: "/pages/whatsapp_bot",
            iconComponent: { name: "cib-whatsapp" },
          },
        ]
      : []),
    ...(environment.showEmail
      ? [
          {
            name: translate.instant("Navigation.MAIL"),
            url: "/pages/email_bot",
            iconComponent: { name: "cib-gmail" },
          },
        ]
      : []),
    ...(environment.showMessenger
      ? [
          {
            name: translate.instant("Navigation.MESSENGER"),
            url: "/pages/messenger_bot",
            iconComponent: { name: "cib-messenger" },
          },
        ]
      : []),
    ...(environment.showInstagram
      ? [
          {
            name: translate.instant("Navigation.INSTAGRAM"),
            url: "/pages/instagram_bot",
            iconComponent: { name: "cib-instagram" },
          },
        ]
      : []),
    ...(environment.showTelephoneReservation
      ? [
          {
            name: translate.instant("Navigation.TELEPHONE_RESERVATION"),
            url: "/pages/telephone",
            iconComponent: { name: "cil-phone" },
          },
        ]
      : []),
    ...(environment.showApiSource
      ? [
          {
            name: translate.instant("Navigation.API_SOURCE"),
            url: "/pages/api_source",
            iconComponent: { name: "cil-code" },
          },
        ]
      : []),
    {
      name: translate.instant("Navigation.BOOKING_FORM_GUARDS"),
      url: "/pages/booking-form-guards",
      iconComponent: { name: "cil-description" },
    },

    ...(isRestaurantCompany || isTransferCompany || isDefaultPlusCompany
      ? [
          {
            title: true,
            name: translate.instant("Navigation.APPOINTMENTS_SECTION"),
          },
          ...(isRestaurantCompany
            ? [
                {
                  name: translate.instant("Navigation.TABLE_MANAGEMENT"),
                  url: "/pages/table_mangement",
                  iconComponent: { name: "cil-coffee" },
                },
                {
                  name: translate.instant("Navigation.MENU"),
                  url: "/pages/menu",
                  iconComponent: { name: "cil-restaurant" },
                },
              ]
            : []),
          ...(isTransferCompany
            ? [
                {
                  name: translate.instant("Navigation.TRANSFER_MANAGEMENT"),
                  url: "/pages/transfer_management",
                  iconComponent: { name: "cil-car-alt" },
                },
              ]
            : []),
          ...(isDefaultPlusCompany
            ? [
                {
                  name: translate.instant("Navigation.DP_SERVICES"),
                  url: "/pages/defaultplus_services",
                  iconComponent: { name: "cil-bookmark" },
                },
                {
                  name: translate.instant("Navigation.DP_STAFF"),
                  url: "/pages/defaultplus_staff",
                  iconComponent: { name: "cil-people" },
                },
              ]
            : []),
        ]
      : []),

    {
      title: true,
      name: translate.instant("Navigation.BOT_MANAGEMENT_SECTION"), // "Bot Yönetimi"
    },
    ...(mode === "legacy"
      ? environment.showAIManagement
        ? [
            {
              name: translate.instant("Navigation.AI_MANAGEMENT"),
              url: "/pages/ai_management",
              iconComponent: { name: "cib-probot" },
            },
          ]
        : []
      : [
          {
            // Once onboarding is done the survey stops being a training pass
            // and becomes the tenant's stored system data (SPEC T83/D7). The
            // url stays put — renaming the route needs a redirect (D10).
            name: translate.instant(
              onboardingActive
                ? "Navigation.BOT_TRAINING_FORM"
                : "Navigation.SYSTEM_INFO",
            ),
            url: "/pages/bot-training-form",
            iconComponent: { name: "cilTask" },
          },
        ]),

    {
      title: true,
      name: translate.instant("Navigation.SUPPORT"),
    },
    {
      name: translate.instant("Navigation.SUPPORT"),
      url: "/pages/support",
      iconComponent: { name: "cil-chat-bubble" },
    },
    {
      name: translate.instant("Navigation.WHATS_NEW"),
      url: "/pages/release-news",
      iconComponent: { name: "cilNotes" },
    },

    {
      title: true,
      name: translate.instant("Navigation.PAYMENTS_SECTION"),
    },
    {
      name: translate.instant("Navigation.PAYMENTS"),
      url: "/pages/payments",
      iconComponent: { name: "cil-credit-card" },
    },
    {
      name: translate.instant("Navigation.ORDER_HISTORY"),
      url: "/pages/order_history",
      iconComponent: { name: "cil-list" },
    },

    {
      title: true,
      name: translate.instant("Navigation.SETTINGS_SECTION"),
    },
    ...(environment.showSettingsManagement
      ? [
          {
            name: translate.instant("Navigation.SETTINGS_MANAGEMENT"),
            url: "/pages/settings-management",
            iconComponent: { name: "cil-settings" },
          },
        ]
      : []),
  ];
}

export function getAdminNavItems(translate: TranslateService): INavData[] {
  return [
    {
      title: true,
      name: translate.instant("Navigation.ADMIN_SECTION"), // "Admin"
    },
    {
      name: translate.instant("Navigation.USER_MANAGEMENT"),
      url: "/admin/users",
      iconComponent: { name: "cil-user" },
    },
    {
      name: translate.instant("Navigation.SUPPORT"),
      url: "/admin/support",
      iconComponent: { name: "cil-envelope-open" },
    },
  ];
}

/**
 * Page key of a sidebar url (`/dashboard` or `/pages/<path>`), or null when
 * the url is not a page covered by page access.
 */
export function pageKeyForNavUrl(url: unknown): string | null {
  if (typeof url !== "string") return null;
  const path = url.split(/[?#]/)[0];
  if (path === "/dashboard") return "dashboard";
  return path.startsWith("/pages/") ? pageKeyForPath(path.slice("/pages/".length)) : null;
}

/**
 * The nav without the pages `canSee` rejects. A section heading is kept only
 * while at least one page under it is. Items without a page key stay.
 */
export function filterNavByAccess(
  items: INavData[],
  canSee: (pageKey: string) => boolean,
): INavData[] {
  const kept: INavData[] = [];
  let heading: INavData | null = null;

  for (const item of items) {
    if (item.title) {
      heading = item;
      continue;
    }
    const key = pageKeyForNavUrl(item.url);
    if (key !== null && !canSee(key)) continue;
    if (heading) {
      kept.push(heading);
      heading = null;
    }
    kept.push(item);
  }
  return kept;
}

/**
 * Url of the first sidebar page `canSee` accepts, in nav order. Download is
 * shown to every member, so it is the fallback.
 */
export function firstPermittedUrl(
  items: INavData[],
  canSee: (pageKey: string) => boolean,
): string {
  const first = items.find((item) => {
    const key = pageKeyForNavUrl(item.url);
    return key !== null && canSee(key);
  });
  return (first?.url as string | undefined) ?? "/pages/download";
}
